<script lang="tsx" setup>
import { truncateWithHtmlByCharacters } from "@/utils/truncate"
import { computed, ref, watch, onMounted } from "vue"

const props = defineProps<{
  html: string
  characters: number
}>()

const emit = defineEmits<{
  (e: "read-more-clicked"): void
}>()

const containerRef = ref<HTMLElement | null>(null)
const characterBuffer = 50
const breakEquivalentChars = ref(20) // Default value before measurement

// Update break equivalent characters based on container width
onMounted(() => {
  if (containerRef.value) {
    const containerWidth = containerRef.value.clientWidth
    const testElement = document.createElement("span")
    testElement.style.visibility = "hidden"
    testElement.textContent = "X"
    containerRef.value.appendChild(testElement)
    const charWidth = testElement.getBoundingClientRect().width
    containerRef.value.removeChild(testElement)

    const charsPerLine = Math.floor(containerWidth / charWidth)
    breakEquivalentChars.value = Math.max(10, Math.floor(charsPerLine / 2))
  }
  readMoreActive.value = !needsReadMore.value
})

watch(
  () => props.html,
  () => {
    readMoreActive.value = !needsReadMore.value
  },
)

// Count HTML breaks and adjust character count
const countBreaksAndAdjustLength = (html: string) => {
  const brMatches = (html.match(/<br\s*\/?>/gi) || []).length
  const pMatches = (html.match(/<\/p>/gi) || []).length
  return (
    brMatches * breakEquivalentChars.value +
    pMatches * breakEquivalentChars.value
  )
}

const needsReadMore = computed(() => {
  const breakChars = countBreaksAndAdjustLength(props.html)
  return props.html.length + breakChars - characterBuffer > props.characters
})

const readMoreActive = ref(true)

watch(readMoreActive, () => {
  emit("read-more-clicked")
})

const content = computed(() => {
  if (readMoreActive.value) return props.html

  // Adjust character limit based on breaks
  const breakChars = countBreaksAndAdjustLength(props.html)
  const adjustedCharLimit = Math.max(props.characters - breakChars, 0)
  return truncateWithHtmlByCharacters(props.html, adjustedCharLimit)
})

const paragraphs = computed(() => {
  const contentDOM = document.createElement("div")
  contentDOM.innerHTML = content.value
  const paragraphs = contentDOM.querySelectorAll("p")
  if (paragraphs.length === 0) return []
  return Array.prototype.slice.call(paragraphs) as HTMLParagraphElement[]
})
</script>

<template>
  <div>
    <template v-if="!readMoreActive">
      <div ref="containerRef">
        <template v-if="paragraphs.length > 0">
          <p v-for="(paragraph, index) in paragraphs" :key="index">
            <span
              v-html="
                index === paragraphs.length - 1
                  ? paragraph.innerHTML.trimEnd()
                  : paragraph.innerHTML
              "
            />
            <button
              v-if="index === paragraphs.length - 1 && needsReadMore"
              class="inline"
              @click="readMoreActive = true"
            >
              ... <span class="underline link">read more</span>
            </button>
          </p>
        </template>
        <template v-else>
          <span class="inline" v-html="content" />
          <button
            class="inline"
            @click="readMoreActive = true"
            v-if="needsReadMore"
          >
            ... <span class="underline link">read more</span>
          </button>
        </template>
      </div>
    </template>

    <div v-else v-html="content" />
    <button
      v-if="readMoreActive && needsReadMore"
      class="underline link"
      @click="readMoreActive = false"
    >
      read less
    </button>
  </div>
</template>
